import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, User } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[6-9]\d{9}$/;
const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}/;
const disposableEmailDomains = new Set([
  "mailinator.com",
  "10minutemail.com",
  "yopmail.com",
  "guerrillamail.com",
  "trashmail.com",
  "temp-mail.org",
  "maildrop.cc",
  "getnada.com",
  "throwawaymail.com",
  "tempmail.com",
  "guerrillamail.net",
  "mailinator.net",
  "disposablemail.com",
  "fakeinbox.com",
  "sharklasers.com",
]);

const isDisposableEmail = (address) => {
  const domain = address.split("@")[1]?.toLowerCase();
  return domain ? disposableEmailDomains.has(domain) : false;
};

export default function SignUp() {
  const { signup, googleLogin } = useCustomerAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Cynos Sign Up";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Full name is required.");
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();

    if (!emailRegex.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (isDisposableEmail(trimmedEmail)) {
      setError("Please use a real email address, not a temporary or disposable email.");
      return;
    }
    if (!phoneRegex.test(phone.trim())) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    if (!passwordRegex.test(password)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, and a number.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await signup(name.trim(), email.trim().toLowerCase(), phone.trim(), password);
      navigate("/profile", { replace: true });
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Unable to create account.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await googleLogin("/profile");
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Google login failed. Please try again.";
      setError(message);
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-xl rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-2xl shadow-black/20"
      >
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center rounded-full bg-cynos-red/10 p-4 mb-4">
            <User className="h-6 w-6 text-cynos-red" />
          </div>
          <h1 className="text-3xl font-anton uppercase text-white">Create Account</h1>
          <p className="mt-3 text-sm text-zinc-400">Register for Cynos to save your orders, addresses and track shipments.</p>
        </div>

        {error && <div className="mb-6 rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div>}

        <button
          type="button"
          onClick={handleGoogle}
          disabled={loading}
          className="mb-6 flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm uppercase tracking-[0.2em] text-white transition hover:border-cynos-red/30 hover:bg-white/10 disabled:cursor-not-allowed"
        >
          <Mail className="h-4 w-4" />
          Continue with Gmail
        </button>

        <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-zinc-500">
          <span className="h-px flex-1 bg-white/10" />
          or register with email
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="customer@example.com"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Mobile Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="9876543210"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="Create a strong password"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="Retype your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Creating Account" : "Create Account"}
          </button>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-sm text-zinc-400">
          <span>Already have an account?</span>
          <Link to="/signin" className="text-white hover:text-cynos-red font-semibold">Sign In</Link>
        </div>
      </motion.div>
    </main>
  );
}
